import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MagnifyingGlass, ArrowRight, ArrowUpRight, MapPin, GraduationCap,
  UsersThree, Buildings, HandHeart, Flower, Translate, Star, EnvelopeSimple, Check, DeviceMobile, DownloadSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EDITION } from "@/lib/edition";
import { useLanguage } from "@/context/LanguageContext";
import { INDIA_GEO } from "@/data/indiaGeo";
import InstallPopup from "@/components/InstallPopup";
import { useSeo } from "@/lib/seo";

const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const HOME_HUES = [
  "from-orange-500 to-rose-600", "from-emerald-500 to-teal-700", "from-indigo-500 to-violet-700",
  "from-amber-500 to-orange-700", "from-sky-500 to-blue-700", "from-fuchsia-500 to-purple-700",
  "from-lime-500 to-green-700", "from-cyan-500 to-teal-700", "from-red-500 to-rose-700",
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETINGS = ["नमस्ते", "வணக்கம்", "নমস্কার", "నమస్కారం", "ನಮಸ್ಕಾರ", "നമസ്കാരം", "નમસ્તે", "ਸਤ ਸ੍ਰੀ ਅਕਾਲ", "নমস্কাৰ"];

const ICONIC = [
  "IIT", "IIM", "University of Delhi", "JNU", "BHU", "Anna University",
  "Jadavpur University", "University of Mumbai", "Osmania University",
  "AIIMS", "NIT", "St. Stephen's College", "Loyola College", "Presidency University",
];

const STATE_META = {
  "Kerala": { hue: "from-emerald-600 to-teal-700" },
  "Uttar Pradesh": { hue: "from-orange-600 to-amber-700" },
  "Maharashtra": { hue: "from-indigo-700 to-violet-800" },
  "Tamil Nadu": { hue: "from-rose-600 to-red-700" },
  "Karnataka": { hue: "from-amber-600 to-orange-700" },
  "West Bengal": { hue: "from-red-700 to-rose-800" },
  "Rajasthan": { hue: "from-pink-600 to-fuchsia-700" },
  "Gujarat": { hue: "from-teal-600 to-cyan-700" },
  "Madhya Pradesh": { hue: "from-purple-700 to-indigo-800" },
  "Bihar": { hue: "from-lime-600 to-green-700" },
  "Telangana": { hue: "from-cyan-700 to-sky-800" },
  "Andhra Pradesh": { hue: "from-sky-600 to-blue-700" },
};

// English source string for the hero subheading; translated on demand via useLanguage().
const HERO_SUB = "From village schools to the IITs — {n}+ Indian institutions, their alumni communities, teachers and memories, all in one place. Find your batch. Rebuild your circle.";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

export default function IndiaLanding() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [states, setStates] = useState([]);
  const [total, setTotal] = useState(0);
  const [installOpen, setInstallOpen] = useState(false);
  const { t, lang, setLang, langs, translating } = useLanguage();

  useSeo(null, "IndiaAlumni — Reconnect with your Indian alma mater. Find your school, college, batch, classmates and teachers across 83,000+ Indian institutions. Thank a teacher, mentor a student, preserve memories and build your legacy.", null, { canonicalPath: "/" });

  useEffect(() => {
    axios.get(`${API}/institutions/locations`, { params: { country: "India" } })
      .then(({ data }) => {
        const india = (data || []).find((c) => c.country === "India") || (data && data[0]);
        const st = (india?.states || []).filter((s) => s.state && s.state !== "Unknown");
        st.sort((a, b) => (b.count || 0) - (a.count || 0));
        setStates(st);
        setTotal(st.reduce((n, s) => n + (s.count || 0), 0));
      })
      .catch(() => {});
  }, []);

  const topStates = useMemo(() => states.slice(0, 10), [states]);
  const search = (e) => {
    e?.preventDefault();
    navigate(`/explore/institutions?country=India${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`);
  };
  const nf = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `${n}`);

  return (
    <div data-testid="india-landing" className="overflow-x-hidden">
      <InstallPopup open={installOpen} onOpenChange={setInstallOpen} t={t} />
      {/* HERO */}
      <section className="relative isolate">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:py-24">
          <motion.div variants={fade} initial="hidden" animate="show">
            {/* Language switcher */}
            <div className="mb-5 flex flex-wrap items-center gap-1.5" data-testid="lang-switcher">
              <Translate size={16} className="mr-1 text-primary" weight="duotone" />
              {langs.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
                  className={`rounded-full px-3 py-1 text-sm transition-colors ${lang === l.code ? "bg-primary text-primary-foreground" : "border border-border/70 bg-white text-foreground/70 hover:border-primary/40"}`}>
                  {l.native}
                </button>
              ))}
              {translating && (
                <span className="ml-1 inline-flex items-center gap-1.5 text-xs font-medium text-primary/70" data-testid="lang-translating-hint">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary/30 border-t-primary" /> translating…
                </span>
              )}
            </div>
            <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 overflow-hidden text-lg font-medium text-primary/80" data-testid="hero-greetings">
              {GREETINGS.map((g, i) => (
                <motion.span key={g} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 + i * 0.12 }}>
                  {g}{i < GREETINGS.length - 1 && <span className="mx-1 text-border">·</span>}
                </motion.span>
              ))}
            </div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-primary/70" data-testid="hero-tagline">
              {t("Reconnect With Your Past · Build Your Future · Leave Your Legacy")}
            </p>
            <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              {lang === "en"
                ? <>Reconnect with your <span className="text-primary">Indian</span> alma mater.</>
                : t("Reconnect with your Indian alma mater.")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              {t(HERO_SUB).replace("{n}", nf(total || 83000))}
            </p>
            <p className="mt-2 text-xs text-foreground/70" data-testid="ecosystem-line">
              {t("Powered by AlumniPulse™ • Part of the SocialPulse® Network")}
            </p>

            <p className="mb-2 mt-8 flex items-center gap-2 text-base font-semibold text-foreground" data-testid="hero-search-label">
              <MagnifyingGlass size={18} weight="bold" className="text-primary" /> {t("Where did you study?")}
            </p>
            <form onSubmit={search} className="flex max-w-xl flex-col gap-2 sm:flex-row" data-testid="hero-search-form">
              <div className="relative flex-1">
                <MagnifyingGlass className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hero-search-input"
                  placeholder={t("Search IITs, DU, regional colleges & schools…")}
                  className="h-12 rounded-full border-border/70 bg-white pl-11 text-base shadow-sm" />
              </div>
              <Button type="submit" size="lg" className="h-12 gap-2 rounded-full px-6" data-testid="hero-search-btn">
                {t("Search")} <ArrowRight size={18} />
              </Button>
            </form>

            <div className="mt-5 grid grid-cols-2 gap-2.5 sm:max-w-xl lg:grid-cols-4">
              <Link to="/explore/institutions?country=India" data-testid="find-institution-btn">
                <Button variant="outline" size="sm" className="w-full gap-1.5 rounded-full"><Buildings size={16} /> {t("Find My Institution")}</Button>
              </Link>
              <Link to="/register?next=%2Ffind" data-testid="find-classmate-btn">
                <Button variant="outline" size="sm" className="w-full gap-1.5 rounded-full"><UsersThree size={16} /> {t("Find a Classmate")}</Button>
              </Link>
              <Link to="/find-teacher" data-testid="find-teacher-btn">
                <Button variant="outline" size="sm" className="w-full gap-1.5 rounded-full"><Star size={16} /> {t("Find a Teacher")}</Button>
              </Link>
              <Link to="/register" data-testid="create-profile-btn">
                <Button size="sm" className="w-full gap-1.5 rounded-full"><GraduationCap size={16} /> {t("Create My Profile")}</Button>
              </Link>
            </div>

            <a href={EDITION.other.url} target="_blank" rel="noreferrer" data-testid="global-link-hero"
              className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-secondary">
              {t(EDITION.other.note)} <span className="font-medium text-secondary">{EDITION.other.label}</span>
              <ArrowUpRight size={14} />
            </a>
          </motion.div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={2} className="relative">
            <button
              type="button"
              onClick={() => setInstallOpen(true)}
              data-testid="download-app-hero"
              className="group mb-3 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5"
            >
              <DownloadSimple size={18} weight="bold" /> {t("Download our Web App")}
              <DeviceMobile size={16} weight="fill" className="opacity-80" />
            </button>
            <div className="relative overflow-hidden rounded-[2rem] border border-border/50 shadow-2xl shadow-accent/10">
              <img src="/indiaalumni-hero.jpg" alt="Indian campus heritage" className="h-[360px] w-full object-cover sm:h-[460px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A243B]/55 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-border/60 bg-white/90 px-5 py-4 shadow-xl backdrop-blur sm:block">
              <p className="font-heading text-2xl font-bold text-primary">{nf(total)}+</p>
              <p className="text-xs text-muted-foreground">{t("institutions across India")}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="border-y border-border/60 bg-muted/40">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-5 py-8 sm:grid-cols-4">
          {[
            { icon: Buildings, val: `${nf(total)}+`, label: "Institutions" },
            { icon: MapPin, val: `${states.length || 36}`, label: "States & UTs" },
            { icon: UsersThree, val: `${nf(total)}+`, label: "Alumni communities" },
            { icon: Translate, val: "12+", label: "Regional languages" },
          ].map((s, i) => (
            <motion.div key={s.label} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="flex items-center gap-3" data-testid={`stat-${i}`}>
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 text-primary"><s.icon size={22} weight="duotone" /></div>
              <div>
                <p className="font-heading text-xl font-bold leading-none">{s.val}</p>
                <p className="text-xs text-muted-foreground">{t(s.label)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ICONIC MARQUEE */}
      <section className="overflow-hidden py-10">
        <p className="mb-5 text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("Alumni from every corner of India")}</p>
        <div className="relative flex overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
          <div className="flex shrink-0 animate-[marquee_38s_linear_infinite] gap-3 pr-3">
            {[...ICONIC, ...ICONIC].map((name, i) => (
              <button key={i} onClick={() => navigate(`/explore/institutions?country=India&q=${encodeURIComponent(name)}`)}
                className="whitespace-nowrap rounded-full border border-border/60 bg-white px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:border-primary/50 hover:text-primary">
                {name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* BROWSE BY STATE — bento */}
      <section className="mx-auto max-w-7xl px-5 py-14">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{t("Browse by state")}</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{t("Find your home state")}</h2>
          </div>
          <Link to="/india/locations" className="inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline" data-testid="all-states-link">
            {t("Browse all locations")} <ArrowRight size={15} />
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" data-testid="state-grid">
          {INDIA_GEO.map((s, i) => {
            const hue = HOME_HUES[i % HOME_HUES.length];
            return (
              <button
                key={s.state}
                onClick={() => navigate(`/india/locations/${toSlug(s.state)}`)}
                data-testid={`state-tile-${toSlug(s.state)}`}
                className="group relative overflow-hidden rounded-2xl border border-border/60 p-3 text-left transition-transform duration-200 hover:-translate-y-0.5 hover:border-primary/40">
                <div className={`absolute inset-0 bg-gradient-to-br ${hue} opacity-[0.92]`} />
                <div className="relative flex h-full flex-col justify-between text-white">
                  <MapPin size={16} weight="duotone" className="opacity-90" />
                  <p className="mt-4 font-heading text-sm font-semibold leading-tight">{s.state}</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 rounded-2xl border border-dashed border-secondary/40 bg-secondary/5 p-4 text-center" data-testid="not-found-cta">
          <p className="text-sm font-medium text-foreground">{t("Not found your school / college?")}</p>
          <Link to="/institutions/suggest" className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:underline" data-testid="suggest-from-home">
            {t("Give us the information and we'll build one")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* REGIONAL LANGUAGES + GROUPS */}
      <section className="border-y border-border/60 bg-muted/30 py-14">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><Translate size={14} /> {t("In your language")}</div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{t("Every institution, in your mother tongue.")}</h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">
              {t("Read institution pages and alumni discussions in Malayalam, Hindi, Tamil, Telugu, Bengali, Kannada and more — translated instantly so no one is left out of the conversation.")}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["മലയാളം", "हिन्दी", "தமிழ்", "తెలుగు", "বাংলা", "ಕನ್ನಡ", "मराठी", "ગુજરાતી"].map((l) => (
                <span key={l} className="rounded-full border border-border/70 bg-white px-3 py-1.5 text-sm">{l}</span>
              ))}
            </div>
          </motion.div>
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="rounded-3xl border border-border/60 bg-white p-8 shadow-sm">
            <UsersThree size={40} weight="duotone" className="text-secondary" />
            <h3 className="mt-4 font-heading text-2xl font-semibold">{t("Your batch is waiting.")}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t("Every institution has an alumni community and class-of-year boards. Join, share memories, and plan your next reunion.")}
            </p>
            <Link to="/alumni-groups"><Button className="mt-5 gap-1.5 rounded-full" data-testid="groups-cta"><UsersThree size={16} /> {t("Explore alumni groups")}</Button></Link>
          </motion.div>
        </div>
      </section>

      {/* LEGACY / IN MEMORIAM */}
      <section className="mx-auto max-w-5xl px-5 py-16 text-center">
        <Flower size={36} weight="duotone" className="mx-auto text-secondary" />
        <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{t("Honouring the teachers who shaped us.")}</h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          {t("Permanent profiles and eternal memorials for the gurus, faculty and classmates we've lost — a lasting place to say \"this person taught me\".")}
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link to="/memorials"><Button variant="outline" className="gap-1.5 rounded-full" data-testid="memorials-cta"><Flower size={16} /> {t("In Memoriam")}</Button></Link>
          <Link to="/find-teacher"><Button variant="ghost" className="gap-1.5 rounded-full" data-testid="find-teacher-cta"><Star size={16} /> {t("Find my teacher")}</Button></Link>
        </div>
      </section>

      {/* WEEKLY INDIA UPDATES — digest signup */}
      <DigestSignup t={t} />

      {/* IMPACT CTA — indigo */}
      <section className="relative overflow-hidden bg-[#1A243B] py-16 text-white">
        <div className="absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:18px_18px]" />
        <div className="relative mx-auto max-w-4xl px-5 text-center">
          <HandHeart size={40} weight="duotone" className="mx-auto text-primary" />
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{t("Your alma mater still needs you.")}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/80">
            {t("From scholarships to labs to mentoring — give back to the school or college that made you. IndiaAlumni connects the need with the alumni who can help.")}
          </p>
          <Link to="/impact"><Button size="lg" className="mt-7 gap-2 rounded-full px-7" data-testid="alumni-impact-cta"><HandHeart size={18} /> {t("See how alumni are giving back")}</Button></Link>
        </div>
      </section>
    </div>
  );
}

function DigestSignup({ t }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/news/subscribe`, { email: email.trim(), country: "India" });
      setDone(true);
      toast.success(data.already ? "You're already subscribed — welcome back!" : "Subscribed! Your weekly India update is on its way.");
    } catch (err) {
      toast.error("Couldn't subscribe right now — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-3xl px-5 py-6">
      <div className="rounded-3xl border border-primary/20 bg-primary/5 p-7 text-center sm:p-9" data-testid="india-digest">
        <EnvelopeSimple size={34} weight="duotone" className="mx-auto text-primary" />
        <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">{t("Get weekly India updates")}</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
          {t("New institutions, alumni groups and reunions added across India — straight to your inbox.")}
        </p>
        {done ? (
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary" data-testid="digest-done">
            <Check size={16} weight="bold" /> {t("Subscribed!")}
          </div>
        ) : (
          <form onSubmit={subscribe} className="mx-auto mt-5 flex max-w-md flex-col gap-2 sm:flex-row">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="digest-email"
              placeholder={t("Your email address")} className="h-11 rounded-full border-border/70 bg-white text-base" />
            <Button type="submit" disabled={busy} className="h-11 gap-1.5 rounded-full px-6" data-testid="digest-subscribe">
              <EnvelopeSimple size={15} /> {busy ? t("Subscribing…") : t("Subscribe")}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
